import React, { useRef, useState, useEffect } from 'react';
import { 
  Box, 
  Typography,
  styled,
  TextField,
  Button,
  Collapse,
  IconButton,
  InputAdornment,
  Paper,
  useTheme,
  Slider,
  Avatar,
  Fade,
  Grow,
} from '@mui/material';
import Markdown from 'react-markdown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SendIcon from '@mui/icons-material/Send';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import FeedbackIcon from '@mui/icons-material/Feedback';
import FeedbackView from './FeedbackView';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import { useNavigate } from 'react-router-dom';
import ConfidenceRating from './ConfidenceRating';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import { languages, useLanguage } from '../data/languages';

const ReadingContainer = styled(Box)(({ theme }) => ({
  height: '100%',
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  alignItems: 'center',
  position: 'relative',
  overflow: 'hidden',
}));

const ContentBox = styled(Box)(({ theme }) => ({
  backgroundColor: 'transparent',
  flexGrow: 1,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  padding: theme.spacing(2, 4),
}));

const ScrollIndicator = styled(Box)(({ theme, position }) => ({
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  color: theme.palette.primary.main,
  opacity: 0.6,
  transition: 'opacity 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    opacity: 1,
  },
  ...(position === 'top' ? {
    top: "5%",
  } : {
    bottom: 0,
  }),
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  margin: '0 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  padding: theme.spacing(6, 2),
  '&::before, &::after': {
    content: '"\\201C"',
    position: 'absolute',
    fontSize: '4rem',
    fontFamily: "'Playfair Display', serif",
    color: theme.palette.primary.main,
    opacity: 0.2,
    lineHeight: 1,
    [theme.breakpoints.down('sm')]: {
      fontSize: '3rem',
    },
  },
  '&::before': {
    top: 0,
    left: -10,
    [theme.breakpoints.down('sm')]: {
      left: -5,
    },
  },
  '&::after': {
    content: '"\\201D"',
    bottom: -40,
    right: -10,
    [theme.breakpoints.down('sm')]: {
      bottom: -30,
      right: -5,
    },
  },
}));

const ReadingText = styled(Typography)(({ theme, isFortuneReading }) => ({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '1.3rem',
  lineHeight: 1.8,
  fontWeight: 400,
  letterSpacing: '0.01em',
  color: 'rgba(0, 0, 0, 0.85)',
  padding: theme.spacing(2, 4),
  textIndent: '2em',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.2rem',
    lineHeight: 1.3,
    padding: theme.spacing(1, 2),
    textIndent: '1.5em',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '1.0rem',
    lineHeight: 1.4,
    padding: theme.spacing(1.5, 3),
    textIndent: '1.8em',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '1.4rem',
    lineHeight: 1.6,
    padding: theme.spacing(2, 4),
    textIndent: '2em',
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '1.5rem',
    lineHeight: 1.9,
    padding: theme.spacing(2.5, 5),
  },
  '& strong': {
    color: theme.palette.primary.main,
    fontWeight: 700,
  },
  '& em': {
    fontStyle: 'italic',
    color: 'rgba(0, 0, 0, 0.7)',
    fontWeight: 500,
  },
  marginBottom: theme.spacing(3)
}));


const ActionButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5, 4),
  borderRadius: theme.spacing(3),
  fontSize: '1.2rem',
  fontWeight: 500,
  fontFamily: "'Space Grotesk', sans-serif",
  textTransform: 'none',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
    padding: theme.spacing(1, 2),
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '1.1rem',
    padding: theme.spacing(1.2, 3),
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '1.2rem',
    padding: theme.spacing(1.5, 4),
  },
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
  },
}));

const ActionButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  justifyContent: 'center',
  marginTop: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  [theme.breakpoints.up('md')]: {
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(3),
  },
  [theme.breakpoints.up('lg')]: {
    gap: theme.spacing(2),
    marginTop: theme.spacing(4),
  },
}));

const StressLevelOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(12px)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(4),
  zIndex: 1500,
  animation: 'fadeIn 0.4s ease-out',
  '@keyframes fadeIn': {
    from: { opacity: 0 },
    to: { opacity: 1 }
  }
}));

const BackButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  left: theme.spacing(2),
  backgroundColor: 'rgba(0, 0, 0, 0.05)',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
  },
}));

const HomeButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(4),
  right: theme.spacing(4),
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
  },
  transition: 'all 0.3s ease',
  zIndex: 100,
  width: '48px',
  height: '48px',
}));

const StressLevelComparison = ({ initialLevel, currentLevel, onChange, translations }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [confidenceRating, setConfidenceRating] = useState(currentLevel || initialLevel);

  const handleStressChange = (e, newValue) => {
    setConfidenceRating(newValue);
    // if (onChange) {
    //   onChange(newValue);
    // }
  };

  const handleStressSubmit = () => {
    if (onChange) {
      onChange(confidenceRating);
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '600px' }}>
      <Typography variant="h4" sx={{
        fontFamily: "'Space Grotesk', sans-serif",
        mb: 6,
        textAlign: 'center',
        fontWeight: 600,
        color: 'rgba(10, 10, 30, 0.85)',
      }}>
        {translations.rightReadingView.titles.rateYourFeelings}
      </Typography>

      <ConfidenceRating
        value={confidenceRating}
        onChange={handleStressChange}
        onSubmit={handleStressSubmit}
        buttonText={translations.rightReadingView.buttons.submitRating}
      />
    </Box>
  );
};

export default function RightReadingView({ 
  index, 
  content, 
  title, 
  onScroll, 
  history,
  isFollowUp = false,
  totalItems = 4,
  question,
  onFeedbackChange,
  isFortuneReading = false,
  isSummary = false,
  onAskFollowUp,
  initialStressLevel,
  currentStressLevel,
  fortuneDialogue = ""
}) {
  const containerRef = useRef(null);
  const isLastItem = index === totalItems - 1;
  const [showStressOverlay, setShowStressOverlay] = useState(false);
  const theme = useTheme();
  const lastWheelTime = useRef(Date.now());
  const [isScrolling, setIsScrolling] = useState(false);
  const navigate = useNavigate();
  const currentLang = useLanguage(); // 获取当前语言
  const t = languages[currentLang] || languages.en; // 获取翻译文本

  const handleWheel = (e) => {
    if (!isFortuneReading) {
      const now = Date.now();
      // Only process wheel events if at least 500ms have passed since the last one
      if (now - lastWheelTime.current >= 1000 && !isScrolling) {
        const direction = e.deltaY > 0 ? 1 : -1;
        setIsScrolling(true);
        onScroll({ direction });
        lastWheelTime.current = now;
        
        // Reset scrolling state after animation completes
        setTimeout(() => {
          setIsScrolling(false);
        }, 500);
      }
    }
  };

  const handleScrollClick = (direction) => {
    if (!isFortuneReading) {
      onScroll({ direction });
    }
  };

  const getNavigationText = (direction) => {
    if (isFollowUp) {
      if (direction === 'up') {
        return index === 0 ? '' : t.rightReadingView.navigation.previousQuestion;
      } else {
        return index === totalItems - 1 ? '' : t.rightReadingView.navigation.nextQuestion;
      }
    } else {
      if (direction === 'up') {
        return index === 0 ? '' : t.rightReadingView.navigation.previousCard;
      } else {
        if (index === 2) return t.rightReadingView.navigation.summary;
        if (index === 3) return t.rightReadingView.navigation.feedback;
        if (index === 4) return '';
        return t.rightReadingView.navigation.nextCard;
      }
    }
  };

  return (
    <ReadingContainer
      ref={containerRef}
      onWheel={handleWheel}
    >
      <ContentBox>
        {!isFortuneReading && index > 0 && !isSummary && (
          <ScrollIndicator 
            position="top" 
            onClick={() => handleScrollClick(-1)}
          >
            <KeyboardArrowUpIcon />
            <Typography variant="caption" sx={{ fontSize: '0.85rem', fontFamily: "'Space Grotesk', sans-serif" }}>
              {getNavigationText('up')}
            </Typography>
          </ScrollIndicator>
        )}

        <ContentWrapper>
          <Box sx={{ width: '100%' }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: isFortuneReading ? '1.6rem' : '1.8rem',
                fontWeight: 600,
                color: theme.palette.primary.main,
                mb: 4,
                textAlign: 'center',
                position: 'relative',
                [theme.breakpoints.down('sm')]: {
                  fontSize: isFortuneReading ? '1.3rem' : '1.5rem',
                  mb: 2,
                },
                [theme.breakpoints.up('md')]: {
                  fontSize: isFortuneReading ? '1.5rem' : '1.7rem',
                  mb: 3,
                },
                [theme.breakpoints.up('lg')]: {
                  fontSize: isFortuneReading ? '1.6rem' : '1.8rem',
                  mb: 4,
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -12,
                  left: '50%',
                  width: '50px',
                  height: '3px',
                  backgroundColor: theme.palette.primary.main,
                  opacity: 0.7,
                  transform: 'translateX(-50%)',
                  borderRadius: '2px',
                  [theme.breakpoints.down('sm')]: {
                    bottom: -8,
                    width: '40px',
                    height: '2px',
                  },
                },
              }}
            >
              {title}
            </Typography>
            <Markdown
              components={{
                p: (props) => (
                  <ReadingText 
                    {...props} 
                    isFortuneReading={isFortuneReading}
                    sx={{
                      mb: 3,
                      textIndent: '2em',
                      '&:last-child': {
                        mb: 0,
                      },
                    }}
                  />
                ),
                strong: (props) => (
                  <Box 
                    component="strong" 
                    {...props}
                  />
                ),
                em: (props) => (
                  <Box 
                    component="em" 
                    {...props}
                  />
                ),
              }}
            >
              {content}
            </Markdown>

            {isSummary && (
              <>
                <ActionButtonsContainer>
                  <ActionButton
                    variant="contained"
                    color="primary"
                    onClick={() => setShowStressOverlay(true)}
                    startIcon={<FeedbackIcon />}
                  >
                    {t.rightReadingView.buttons.recordFeeling}
                  </ActionButton>
                  <ActionButton
                    variant="outlined"
                    onClick={onAskFollowUp}
                    startIcon={<QuestionAnswerIcon />}
                  >
                    {t.rightReadingView.buttons.askFollowUp}
                  </ActionButton>
                </ActionButtonsContainer>

                {showStressOverlay && (
                  <StressLevelOverlay>
                    <BackButton onClick={() => setShowStressOverlay(false)}>
                      <ArrowBackIcon />
                    </BackButton>
                    
                    <StressLevelComparison
                      initialLevel={initialStressLevel}
                      currentLevel={currentStressLevel}
                      onChange={onFeedbackChange}
                      translations={t}
                    />
                  </StressLevelOverlay>
                )}
              </>
            )}

            {isFortuneReading && (
              <ActionButtonsContainer>
                <ActionButton
                  variant="outlined"
                  onClick={onAskFollowUp}
                  startIcon={<QuestionAnswerIcon />}
                >
                  {t.rightReadingView.buttons.askFollowUp}
                </ActionButton>
              </ActionButtonsContainer>
            )}
          </Box>
        </ContentWrapper>

        {!isFortuneReading && index < totalItems - 1 && !isSummary && (
          <ScrollIndicator 
            position="bottom" 
            onClick={() => handleScrollClick(1)}
          >
            <Typography variant="caption" sx={{ fontSize: '0.85rem', fontFamily: "'Space Grotesk', sans-serif" }}>
              {getNavigationText('down')}
            </Typography>
            <KeyboardArrowDownIcon />
          </ScrollIndicator>
        )}
      </ContentBox>
      {isFortuneReading && (
        <HomeButton onClick={() => navigate('/')} aria-label="Go to home page">
          <HomeIcon />
        </HomeButton>
      )}
    </ReadingContainer>
  );
}
